import {
  type ButtonFactory,
  type CardFactory,
  type ChipFactory,
  createTheme,
  type CSSVariablesResolver,
  type ExtendComponent,
  type FactoryPayload,
  type InputBaseFactory,
  type InputFactory,
  type MantineColorsTuple,
  type MantineRadius,
  type MantineThemeComponent,
  type MultiSelectFactory,
  type NumberInputFactory,
  type PaperFactory,
  type PasswordInputFactory,
  rem,
  type SegmentedControlFactory,
  type SelectFactory,
  type TableFactory,
  type TextInputFactory,
  type TooltipFactory,
} from '@mantine/core';
import { themeToVars } from '@mantine/vanilla-extract';
import {
  accentColours,
  blueSchemeColours,
  blueSchemeDarkVariants,
  controlBorderWidth,
  controlHeight,
  darkSchemeColours,
  dimmedInk,
  featureCardRadius,
  fontFamily,
  fontFamilyMonospace,
  greyColours,
  headingFontWeight,
  headingScale,
  markdownRuleColours,
  navyColours,
  overlayColours,
  redColours,
  serifFontFamily,
  tabularNums,
} from './tokens';
import { tintInkScaleVariable, variantColorResolver } from './variant-colours';

const accent: MantineColorsTuple = [...accentColours];
const navy: MantineColorsTuple = [...navyColours];
const dark: MantineColorsTuple = [...darkSchemeColours];
const red: MantineColorsTuple = [...redColours];
const gray: MantineColorsTuple = [...greyColours];

/**
 * The radii a card takes (gap register G5). `standard` is the theme's `Paper`/`Card` default, so
 * a card only names a radius when it is one of Charlie's larger account and plan cards:
 * `<Paper withBorder radius={cardRadii.feature}>`. Also on `theme.other.cardRadii`.
 */
export const cardRadii = {
  /** 16px — Mantine's `lg` step. Listing, chart and section cards. */
  standard: 'lg',
  /** 24px — account and plan cards. Off Mantine's scale (its `xl` is 32px), so a number. */
  feature: featureCardRadius,
} as const satisfies Record<string, MantineRadius>;

/**
 * Charlie's control treatment — 48px pill, 2px Primary outline or a Primary 20 fill — belongs to a
 * control at its DEFAULT size: one that passes no `size` at all. Any explicit `size` (including
 * `"sm"`, Mantine's own default) opts back into Mantine's stock geometry and colours, which is what
 * keeps the dense admin tables, the chat composer and the sidebar rename field as they were. The
 * pill radius itself applies at every size.
 */
const CONTROL_HEIGHT = rem(controlHeight);
const CONTROL_BORDER = `${rem(controlBorderWidth)} solid var(--charlie-control-border)`;

/**
 * Mantine's stylesheets read these variables, but its `vars` resolver types only list the ones
 * Mantine itself sets. Spreading them through here keeps the escape in one documented place.
 */
function stylesheetVars(variables: Record<`--${string}`, string>) {
  return variables;
}

/**
 * `Component.extend(…)` without importing the component. Mantine's `extend` is a typed identity
 * function, so this is the same thing typed from the component's factory alone. It matters
 * because this module is evaluated wherever a `.css.ts` imports `vars`: referencing the runtime
 * components here made every such stylesheet compile pull in their module graphs, and a lazily
 * loaded chunk's stylesheet (MdTableGrid → typography.css) slowed from ~0.4s to over 1s under the
 * full test suite.
 */
function extend<Payload extends FactoryPayload>(
  input: ExtendComponent<Payload>,
): MantineThemeComponent {
  return input as MantineThemeComponent;
}

export const theme = createTheme({
  colors: {
    // Brand accent occupies `blue` so existing color="blue" usages stay branded
    blue: accent,
    navy,
    // Navy-tinted dark scale: the dark colour scheme derives body/surface/border
    // colours from here, giving the Charlie deep-navy dark theme
    dark,
    // Destructive red occupies `red`, so every color="red" (delete buttons,
    // confirm modals, error alerts) renders the Charlie destructive colour
    red,
    // Figma neutrals occupy `gray`, so color="gray" and the semantic
    // light-scheme roles derived from it (default border, dimmed text) stop
    // using Mantine's blue-tinted defaults
    gray,
  },
  primaryColor: 'blue',
  primaryShade: 7, // 0 to 9 (light to dark)
  fontFamily,
  fontFamilyMonospace,
  defaultRadius: 'md',
  // Mantine's own transitions (menus, modals, popovers, collapses) honour the OS setting
  respectReducedMotion: true,
  variantColorResolver,
  headings: {
    // Page, section and card titles are the Newsreader serif at regular weight (decision D-1,
    // gap register G3), as the agent's markdown headings already were. This reverses the earlier
    // choice to keep chrome headings sans: UI labels, buttons, table headers and the nav stay
    // sans because they are not `Title`s — a control label that renders a `Title` is a bug.
    fontFamily: serifFontFamily,
    fontWeight: String(headingFontWeight),
    sizes: {
      h1: {
        fontSize: rem(headingScale.h1.fontSize),
        lineHeight: String(headingScale.h1.lineHeight),
      },
      h2: {
        fontSize: rem(headingScale.h2.fontSize),
        lineHeight: String(headingScale.h2.lineHeight),
      },
      h3: {
        fontSize: rem(headingScale.h3.fontSize),
        lineHeight: String(headingScale.h3.lineHeight),
      },
      h4: {
        fontSize: rem(headingScale.h4.fontSize),
        lineHeight: String(headingScale.h4.lineHeight),
      },
      h5: {
        fontSize: rem(headingScale.h5.fontSize),
        lineHeight: String(headingScale.h5.lineHeight),
      },
      h6: {
        fontSize: rem(headingScale.h6.fontSize),
        lineHeight: String(headingScale.h6.lineHeight),
      },
    },
  },
  other: { cardRadii },
  components: {
    Button: extend<ButtonFactory>({
      defaultProps: { radius: 'xl' },
      vars: (_theme, props) => {
        if (props.size !== undefined) return { root: {} };
        const neutral = props.color === undefined;
        const outlined = neutral && (props.variant === 'default' || props.variant === 'outline');
        const tinted = neutral && props.variant === 'light';
        return {
          root: {
            '--button-height': CONTROL_HEIGHT,
            '--button-padding-x': rem(24),
            ...(outlined && {
              '--button-bd': CONTROL_BORDER,
              '--button-color': 'var(--mantine-color-text)',
              '--button-hover': 'var(--mantine-color-default-hover)',
            }),
            ...(tinted && {
              '--button-bg': 'var(--charlie-control-fill)',
              '--button-hover': 'var(--charlie-control-fill-hover)',
              '--button-color': 'var(--mantine-color-text)',
            }),
          },
        };
      },
    }),

    // One rule for every Input-based field: Mantine resolves `theme.components.Input.vars` for
    // TextInput, Select, MultiSelect, NumberInput and PasswordInput alike, with that field's
    // merged props. Textarea defaults to `size="sm"`, so it never takes the 48px height.
    //
    // `InputBase` (under TextInput, Select and NumberInput) and `MultiSelect` ship `size: 'sm'` as
    // a Mantine default, and Input's resolver sees the merged value — so an untouched field looked
    // exactly like an explicit `size="sm"` and never got the pill. Clearing that default here makes
    // "no size" observable again; an explicit size still arrives, and Mantine's stylesheet falls
    // back to its `sm` metrics for everything this does not set.
    InputBase: extend<InputBaseFactory>({ defaultProps: { size: undefined } }),
    Input: extend<InputFactory>({
      vars: (_theme, props) => {
        if (props.size !== undefined || props.variant === 'unstyled') return { wrapper: {} };
        const outlined = (props.variant ?? 'default') === 'default' && !props.error;
        return {
          wrapper: {
            '--input-height': CONTROL_HEIGHT,
            // MultiSelect is multiline (pills wrap); centre one row of pills in the 48px field
            ...(props.multiline && { '--input-padding-y': rem(11) }),
            // Not set while in error, so Mantine's error border still wins
            ...(outlined && stylesheetVars({ '--input-bd': 'var(--charlie-control-border)' })),
          },
        };
      },
      styles: (_theme, props) =>
        props.size === undefined && (props.variant ?? 'default') === 'default'
          ? { input: { borderWidth: rem(controlBorderWidth) } }
          : {},
    }),
    TextInput: extend<TextInputFactory>({ defaultProps: { radius: 'xl' } }),
    PasswordInput: extend<PasswordInputFactory>({ defaultProps: { radius: 'xl' } }),
    Select: extend<SelectFactory>({ defaultProps: { radius: 'xl' } }),
    MultiSelect: extend<MultiSelectFactory>({ defaultProps: { radius: 'xl', size: undefined } }),
    NumberInput: extend<NumberInputFactory>({ defaultProps: { radius: 'xl' } }),

    // Chip is already a pill (Mantine draws it at radius 1000rem); only the height follows Charlie
    Chip: extend<ChipFactory>({
      vars: (_theme, props) =>
        props.size === undefined
          ? {
              root: {
                '--chip-size': CONTROL_HEIGHT,
                '--chip-padding': rem(20),
                '--chip-checked-padding': rem(14),
              },
            }
          : { root: {} },
    }),
    SegmentedControl: extend<SegmentedControlFactory>({
      defaultProps: { radius: 'xl' },
      // 4px root padding either side + 9px label padding either side + one 14px line at
      // Mantine's 1.55 line height ≈ 48px
      vars: (_theme, props) =>
        props.size === undefined
          ? { root: { '--sc-padding': `${rem(9)} ${rem(20)}` } }
          : { root: {} },
    }),

    Paper: extend<PaperFactory>({
      defaultProps: { radius: cardRadii.standard },
      vars: () => ({
        root: stylesheetVars({ '--paper-border-color': 'var(--charlie-card-border)' }),
      }),
    }),
    Card: extend<CardFactory>({ defaultProps: { radius: cardRadii.standard } }),

    // Wrap long help text rather than running a single line across the viewport; short labels
    // still shrink to fit, because this is a ceiling and not a width
    Tooltip: extend<TooltipFactory>({ defaultProps: { multiline: true, maw: rem(280) } }),

    // Figures in every table line up in columns (decision D-7). Tabular numerals change only the
    // digits' advance widths, so it is harmless on cells of words.
    Table: extend<TableFactory>({ styles: { table: tabularNums } }),
  },
});

/**
 * The name the chat shell passes to Mantine's `<Container size>`: a non-preset string resolves
 * to `var(--container-size-<name>)`, which is what lets the transcript and the composer share
 * ONE responsive measure instead of two copies of a number. Exported rather than spelled out at
 * each call site because the two containers drifting apart is the exact defect 885705c fixed.
 */
export const chatContainerSize = 'chat';

// CSS variables resolver for custom global variables
// --charlie-md-surface / --charlie-md-rule back the Charlie markdown style
// guide's surface cards (#ECECEC "Def Stroke") and 2px rules (navy @ ~35%)
export const cssVariablesResolver: CSSVariablesResolver = (_theme) => ({
  variables: {
    /**
     * The conversation column's measure: 720px up to ~1385px of viewport, then growing with
     * the screen to an 800px ceiling reached at ~1540px.
     *
     * The floor is today's `size="sm"` (45rem), so nothing narrower than a laptop changes at
     * all. The ceiling is a characters-per-line budget, not a pixel one: the ~900px ceiling
     * usually quoted for comfortable reading assumes 16px prose, and Charlie's body copy is
     * mdB **14px** (`bodyFontSize`, apps/portal/src/components/chat/typography.css.ts).
     * Holding the line length rather than the width gives 900 x 14/16 = 787px of measure, and
     * a Mantine Container spends `spacing.md` either side, so the border-box ceiling is ~819px
     * — rounded to a flat 50rem. The 80px it adds over the old fixed 720 goes to the blocks
     * that actually wanted it: tables (whose cells are `white-space: nowrap`), code blocks and
     * charts.
     */
    '--container-size-chat': 'clamp(45rem, 52vw, 50rem)',
  },
  light: {
    // Ink (gap register G1): Charlie sets text and headings in brand Primary navy, not black.
    // `--mantine-color-black` itself stays black, so an explicit c="black" still means black.
    '--mantine-color-text': 'var(--mantine-color-navy-7)',
    '--mantine-color-bright': 'var(--mantine-color-navy-7)',
    '--mantine-color-default-color': 'var(--mantine-color-navy-7)',
    // Secondary text at AA (Mantine's grey[6] is 3.98:1 on white) — see `dimmedInk`
    '--mantine-color-dimmed': dimmedInk,
    // Mantine's light error (red[6], #E25353) is 3.75:1 on white, under AA for the 12px input
    // error line; red[8] clears 6:1 (Lane T sweep)
    '--mantine-color-error': 'var(--mantine-color-red-8)',
    '--text-color': 'var(--mantine-color-text)',
    '--charlie-md-surface': greyColours[2],
    '--charlie-md-rule': markdownRuleColours.Light,
    // Controls (G4): 2px Primary outline, Primary 20 fill
    '--charlie-control-border': 'var(--mantine-color-navy-7)',
    '--charlie-control-fill': 'var(--mantine-color-navy-1)',
    '--charlie-control-fill-hover': 'var(--mantine-color-navy-2)',
    // Cards (G5): the style guide's "Def Stroke" #ECECEC
    '--charlie-card-border': 'var(--mantine-color-gray-2)',
    // An inset hairline that keeps a mostly-white image (a flag tile) from dissolving into a
    // white card; light ink on dark grounds
    '--charlie-hairline': overlayColours.Hairline,
    // The accent as a mark on the scheme ground (the page loader): the brand stop on white
    '--charlie-accent-ink': 'var(--mantine-color-blue-7)',
    [tintInkScaleVariable]: '1',
  },
  dark: {
    '--text-color': 'var(--mantine-color-text)',
    '--charlie-md-surface': 'var(--mantine-color-dark-5)',
    '--charlie-md-rule': markdownRuleColours.Dark,
    // The same roles one step up the scheme's own scale; the blue scheme re-points `dark-*`, so
    // these follow it without restating
    '--charlie-control-border': 'var(--mantine-color-dark-3)',
    '--charlie-control-fill': 'var(--mantine-color-dark-5)',
    '--charlie-control-fill-hover': 'var(--mantine-color-dark-4)',
    '--charlie-card-border': 'var(--mantine-color-dark-4)',
    '--charlie-hairline': overlayColours.HairlineDark,
    // The brand accent stop is 2.7:1 on the blue page; Accent 40 clears 3:1 on the dark and blue
    // pages and cards
    '--charlie-accent-ink': 'var(--mantine-color-blue-3)',
    [tintInkScaleVariable]: '0',
    // A navy fill is the dark and blue page colour itself (1.0:1 in blue), so a filled navy badge
    // or button lost its shape. Primary 80 — the blue scheme's own surface stop — keeps white text
    // at 7.3:1 and reads as a pill on the page.
    '--mantine-color-navy-filled': 'var(--mantine-color-navy-5)',
    '--mantine-color-navy-filled-hover': 'var(--mantine-color-navy-4)',
    // Mantine's dark error (red[8]) is 2.5:1 on the dark input ground; red[4] clears 6:1. The blue
    // scheme takes a paler step again (see blueSchemeVariables).
    '--mantine-color-error': 'var(--mantine-color-red-4)',
  },
});

// The document-root attribute that selects the additive "blue" scheme. Blue runs
// on top of Mantine's dark scheme (the navy chrome is dark-adjacent, so dark is
// the right base for contrast and derived colours) — see `blueSchemeColours`.
export const charlieSchemeAttribute = 'data-charlie-scheme';

// CSS variable overrides applied under `[data-charlie-scheme='blue']`. Swapping
// the `dark-*` stops is enough to move body, surfaces, borders and text together,
// because Mantine's dark semantic variables all reference them.
export const blueSchemeVariables: Record<string, string> = {
  ...Object.fromEntries(
    blueSchemeColours.map((colour, index) => [`--mantine-color-dark-${String(index)}`, colour]),
  ),
  // Filled/outline variants of the `dark` palette are literal rgba() in Mantine's
  // stylesheet rather than scale references, so they need restating.
  '--mantine-color-dark-filled': blueSchemeColours[8],
  '--mantine-color-dark-filled-hover': blueSchemeColours[9],
  '--mantine-color-dark-light': blueSchemeDarkVariants.Light,
  '--mantine-color-dark-light-hover': blueSchemeDarkVariants.LightHover,
  '--mantine-color-dark-outline-hover': blueSchemeDarkVariants.OutlineHover,
  // Markdown surface cards sit at Primary 80 flat (not the hover stop), and the
  // rules read as white at low alpha against navy.
  '--charlie-md-surface': blueSchemeColours[6],
  '--charlie-md-rule': markdownRuleColours.Blue,
  // Error text and border sit on Primary 80 inputs here: red[8] was 1.26:1, red[4] is 3.0:1;
  // red[2] clears 4.8:1 on the input and 8.9:1 on the page
  '--mantine-color-error': 'var(--mantine-color-red-2)',
};

// Vanilla Extract CSS variables from theme
export const vars = themeToVars(theme);
